import React, { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  makeStyles,
  Typography,
  Box,
  IconButton,
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslate } from 'react-admin'
import useHistoryPush from '../../hooks/useHistoryPush'
import getSeasonTeamPlayers from '../../Apis/player/getSeasonTeamPlayers'
import removePlayerFromSeasonTeam from '../../Apis/player/removePlayerFromSeasonTeam'
import QueryKeys from '../../utils/constants/queryKeys'
import LoaderDots from '../../components/loaders/LoaderDots/LoaderDots'
import COLORS from '../../utils/constants/colors'
import AddButton from '../../components/ui/AddButton/AddButton'
import SeasonTeamPlayerCreateDrawer from './SeasonTeamPlayerCreateDrawer'
import PlayerAssignModal from './PlayerAssignModal'
import DeleteModal from '../../components/DeleteModal/DeleteModal'
import USER_PERMISSIONS from '../../utils/constants/userPermissions'
import IsPermitted from '../../components/IsPermitted/IsPermitted'
import { useCmsScope } from '../../context/useCmsScope'

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    padding: '2%',
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  noData: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
}))

const SeasonTeamPlayersPage = ({ seasonTeamId }) => {
  const classes = useStyles()
  const translate = useTranslate()
  // A paired club reads the federation's roster: no assigning, adding, editing or removing.
  const { orgId, readOnly } = useCmsScope()
  const queryClient = useQueryClient()
  const { redirect } = useHistoryPush()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedPlayer, setSelectedPlayer] = useState(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [playerToDelete, setPlayerToDelete] = useState(null)
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)

  const { data, isLoading } = useQuery(
    [QueryKeys.GET_SEASON_TEAM_PLAYERS, orgId, seasonTeamId],
    () => getSeasonTeamPlayers(orgId, seasonTeamId),
    { enabled: Boolean(orgId && seasonTeamId) }
  )

  const deletePlayerMutation = useMutation(
    (seasonTeamPlayerId) => removePlayerFromSeasonTeam(orgId, seasonTeamPlayerId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_SEASON_TEAM_PLAYERS])
        setIsDeleteModalOpen(false)
        setPlayerToDelete(null)
      },
    }
  )

  const handleAddPlayer = () => {
    setSelectedPlayer(null)
    setIsDrawerOpen(true)
  }

  const handleEditPlayer = (playerId) => {
    const player = players.find((p) => p.id === playerId)
    setSelectedPlayer(player)
    setIsDrawerOpen(true)
  }

  const handleDeletePlayer = (playerId) => {
    const player = players.find((p) => p.id === playerId)
    setPlayerToDelete(player)
    setIsDeleteModalOpen(true)
  }

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '-'
    const today = new Date()
    const birthDate = new Date(dateOfBirth)
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  const getPositions = (player) => {
    if (!player.personalData?.positions || player.personalData.positions.length === 0) return '-'
    return player.personalData.positions.map((p) => p.name).join(', ')
  }

  if (isLoading) {
    return (
      <Box className={classes.loaderContainer}>
        <LoaderDots style={{ fill: COLORS.orange, width: 70, height: 70 }} />
      </Box>
    )
  }

  const players = Array.isArray(data) ? data : []

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        {!readOnly && (
        <Box display="flex" style={{ gap: 10 }}>
          <IsPermitted permission={USER_PERMISSIONS.SETUP_CMS}>
            <AddButton label='Assign Player' onClick={() => setIsAssignModalOpen(true)} />
          </IsPermitted>
          <IsPermitted permission={USER_PERMISSIONS.SETUP_CMS}>
            <AddButton
              label={translate('ra.buttons.addPlayer')}
              onClick={handleAddPlayer}
              filled
            />
          </IsPermitted>
        </Box>
        )}
      </div>

      <TableContainer component={Paper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Jersey Number</TableCell>
              <TableCell>Height</TableCell>
              <TableCell>Age</TableCell>
              <TableCell>Positions</TableCell>
              {!readOnly && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {players.length === 0 ? (
              <TableRow>
                <TableCell colSpan={readOnly ? 5 : 6}>
                  <Typography className={classes.noData} variant="body1">
                    No players found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              players.map((player) => (
                <TableRow key={player.id} className={classes.tableRow} onClick={() => redirect(`/competition/stats/player/${player.id}`)}>
                  <TableCell>
                    {player.personalData?.firstName} {player.personalData?.lastName}
                  </TableCell>
                  <TableCell>{player.jerseyNumber ?? '-'}</TableCell>
                  <TableCell>{player.personalData?.height ? `${player.personalData.height} cm` : '-'}</TableCell>
                  <TableCell>{calculateAge(player.personalData?.dateOfBirth)}</TableCell>
                  <TableCell>{getPositions(player)}</TableCell>
                  {!readOnly && (
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleEditPlayer(player.id) }}
                      aria-label="edit"
                    >
                      <EditIcon fontSize="small" style={{ fill: COLORS.orange }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleDeletePlayer(player.id) }}
                      aria-label="delete"
                    >
                      <DeleteIcon fontSize="small" style={{ fill: COLORS.purple }} />
                    </IconButton>
                  </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!readOnly && (
      <>
      <SeasonTeamPlayerCreateDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        player={selectedPlayer}
        seasonTeamId={seasonTeamId}
      />

      <PlayerAssignModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        seasonTeamId={seasonTeamId}
        orgId={orgId}
        currentPlayers={players}
      />

      <DeleteModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title='Delete Player'
        subtitle={`Are you sure you want to delete ${playerToDelete?.personalData?.firstName} ${playerToDelete?.personalData?.lastName}?`}
        buttonLabel={translate('ra.action.delete') || 'Delete'}
        handleDelete={() => playerToDelete && deletePlayerMutation.mutate(playerToDelete.id)}
        loading={deletePlayerMutation.isLoading}
      />
      </>
      )}
    </div>
  )
}

export default SeasonTeamPlayersPage
